import { useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

const TextEditor = ({
  editorValue,
  height,
  setEditorValue,
  showHeader = true,
}) => {
  const editorRef = useRef(null);

  const handleEditorChange = (newContent) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = newContent;

    const tables = tempDiv.querySelectorAll("table");
    tables.forEach((table) => {
      table.style.borderCollapse = "collapse";

      const cells = table.querySelectorAll("td, th");
      cells.forEach((cell) => {
        cell.style.border = "1px solid #ccc";
        cell.style.verticalAlign = "top";

        if (table.classList.contains("medical-table")) {
          cell.style.padding = "1px 3px";
          cell.style.lineHeight = "0.85";
          cell.style.fontSize = "11px";
          cell.style.height = "14px";
          cell.style.whiteSpace = "nowrap";
        } else if (table.classList.contains("compact-table")) {
          cell.style.padding = "1px 4px";
          cell.style.lineHeight = "0.9";
          cell.style.fontSize = "11px";
          cell.style.height = "16px";
        } else {
          cell.style.padding = "2px 6px";
          cell.style.lineHeight = "1";
          cell.style.fontSize = "13px";
        }

        const paragraphs = cell.querySelectorAll("p");
        paragraphs.forEach((p) => {
          p.style.margin = "0";
          p.style.padding = "0";
          p.style.lineHeight = "inherit";
        });
      });
    });

    setEditorValue(tempDiv.innerHTML);
  };

  return (
    <Editor
      onInit={(_evt, editor) => (editorRef.current = editor)}
      value={editorValue}
      onEditorChange={handleEditorChange}
      init={{
        height: height || 400,
        menubar: showHeader,
        branding: false,
        toolbar_mode: "sliding",
        promotion: false,

        skin_url: "/tinymce/skins/ui/oxide",
        content_css: "/tinymce/skins/content/default/content.css",

        plugins: [
          "advlist",
          "anchor",
          "autolink",
          "charmap",
          "code",
          "emoticons",
          "fullscreen",
          "help",
          "image",
          "insertdatetime",
          "link",
          "lists",
          "media",
          "preview",
          "searchreplace",
          "table",
          "visualblocks",
          "wordcount",
          "nonbreaking",
        ],

        font_size_formats:
          "8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
        line_height_formats:
          "0 0.5 0.8 0.9 1 1.1 1.2 1.3 1.4 1.5 1.6 1.8 2 2.5 3",
        toolbar:
          "undo redo | fontfamily fontsize lineheight | bold italic underline strikethrough | " +
          "forecolor backcolor | alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | link image media emoticons charmap | " +
          "insertdatetime table compacttable makecompact | code fullscreen preview | searchreplace | help",

        table_toolbar:
          "tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablecellprops",
        table_class_list: [
          { title: "Default", value: "" },
          { title: "Compact Table", value: "compact-table" },
          { title: "Medical/Data Table", value: "medical-table" },
          { title: "Bordered Table", value: "bordered-table" },
        ],
        table_cell_class_list: [
          { title: "None", value: "" },
          { title: "Small Cell", value: "small-cell" },
          { title: "Compact Cell", value: "compact-cell" },
        ],
        table_row_class_list: [
          { title: "None", value: "" },
          { title: "Small Row", value: "small-row" },
          { title: "Compact Row", value: "compact-row" },
        ],

        content_style: `
          body { 
            font-family: Helvetica, Arial, sans-serif; 
            font-size: 14px;
            line-height: 1.4;
          }
          table {
            border-collapse: collapse !important;
            line-height: 1 !important;
          }
          table td, table th {
            border: 1px solid #ccc !important;
            padding: 2px 6px !important;
            vertical-align: top !important;
            line-height: 1 !important;
            height: auto !important;
            min-height: 18px !important;
            font-size: 13px !important;
          }
          table p {
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
          }
          table br {
            line-height: 1 !important;
          }
          table.compact-table {
            line-height: 0.9 !important;
          }
          table.compact-table td,
          table.compact-table th {
            padding: 1px 4px !important;
            line-height: 0.9 !important;
            font-size: 11px !important;
            height: 16px !important;
          }
          table.medical-table td,
          table.medical-table th {
            padding: 1px 3px !important;
            line-height: 0.85 !important;
            font-size: 11px !important;
            height: 14px !important;
            white-space: nowrap !important;
          }
          table * {
            line-height: inherit !important;
          }
          table:not([class]) td,
          table:not([class]) th {
            padding: 2px 4px !important;
            line-height: 1 !important;
            height: auto !important;
          }
        `,

        extended_valid_elements:
          "table[*],tr[*],td[*],th[*],p[*],span[*],div[*],u[*]",
        valid_styles: {
          "*": "font-family,border,border-collapse,padding,margin,line-height,height,font-size,vertical-align,white-space,width,color,background-color,text-align,text-decoration",
        },

        inline_styles: true,
        force_p_newlines: false,

        setup: function (editor) {
          editor.ui.registry.addButton("compacttable", {
            text: "Medical Table",
            onAction: function () {
              editor.execCommand("mceInsertTable", false, {
                rows: 3,
                cols: 2,
                options: {
                  headerRows: 1,
                  headerCols: 0,
                },
              });

              setTimeout(() => {
                const table = editor.selection.getNode().closest("table");
                if (table) {
                  editor.dom.addClass(table, "medical-table");
                  const cells = table.querySelectorAll("td, th");
                  cells.forEach((cell) => {
                    cell.style.lineHeight = "0.85";
                    cell.style.padding = "1px 3px";
                    cell.style.fontSize = "11px";
                    cell.style.border = "1px solid #ccc";
                    cell.style.verticalAlign = "top";
                  });
                  editor.fire("change");
                }
              }, 100);
            },
          });

          editor.ui.registry.addButton("makecompact", {
            text: "Make Compact",
            onAction: function () {
              const node = editor.selection.getNode();
              if (!node) return;
              const table = node.closest("table");
              if (table) {
                editor.dom.addClass(table, "medical-table");
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  cell.style.lineHeight = "0.85";
                  cell.style.padding = "1px 3px";
                  cell.style.fontSize = "11px";
                  cell.style.height = "14px";
                  cell.style.border = "1px solid #ccc";
                  cell.style.verticalAlign = "top";
                });
                editor.fire("change");
              }
            },
          });

          editor.on("init", function () {
            setTimeout(() => {
              const doc = editor.getDoc();
              if (!doc) return;
              const tables = doc.querySelectorAll("table");
              tables.forEach((table) => {
                table.style.borderCollapse = "collapse";
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  if (!cell.style.border) {
                    cell.style.lineHeight = "1";
                    cell.style.padding = "2px 4px";
                    cell.style.border = "1px solid #ccc";
                    cell.style.verticalAlign = "top";
                  }
                });
              });
              editor.fire("change");
            }, 500);
          });

          editor.on("NewTable", function () {
            setTimeout(() => {
              const table = editor.selection.getNode().closest("table");
              if (table) {
                table.style.borderCollapse = "collapse";
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  cell.style.border = "1px solid #ccc";
                  cell.style.padding = "2px 6px";
                  cell.style.verticalAlign = "top";
                  cell.style.lineHeight = "1";
                  cell.style.fontSize = "13px";
                });
                editor.fire("change");
              }
            }, 100);
          });

          editor.on("SetContent", function () {
            setTimeout(() => {
              const doc = editor.getDoc();
              if (!doc) return;
              const tables = doc.querySelectorAll("table");
              tables.forEach((table) => {
                if (!table.style.borderCollapse) {
                  table.style.borderCollapse = "collapse";
                }
                const cells = table.querySelectorAll("td, th");
                cells.forEach((cell) => {
                  if (!cell.style.border) {
                    cell.style.border = "1px solid #ccc";
                    cell.style.padding = "2px 6px";
                    cell.style.verticalAlign = "top";
                    cell.style.lineHeight = "1";
                    cell.style.fontSize = "13px";
                  }
                });
              });
            }, 100);
          });
        },
      }}
    />
  );
};

export default TextEditor;
